import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AlertTriangle, Plus, Trash2, X } from 'lucide-react';
import type { DeletePreview } from '@pujolisique/shared';
import { deleteFiles, previewDelete, usePlaylistMutations, usePlaylists } from '../api/hooks';
import { queryClient } from '../queryClient';
import { useUi } from '../ui/store';
import { toast, useToasts } from '../ui/toast';
import { fmtBytes, plural } from '../lib/format';

/** Menu contextuel (clic droit, bouton « … ») */
export function ContextMenu() {
  const menu = useUi((s) => s.menu);
  const close = useUi((s) => s.closeMenu);
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: 0, top: 0 });

  useLayoutEffect(() => {
    if (!menu || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const left = Math.min(menu.x, window.innerWidth - r.width - 8);
    const top = menu.y + r.height > window.innerHeight - 8 ? Math.max(8, menu.y - r.height) : menu.y;
    setPos({ left: Math.max(8, left), top });
    ref.current.querySelector<HTMLButtonElement>('button')?.focus();
  }, [menu]);

  useEffect(() => {
    if (!menu) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const items = [...(ref.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? [])];
        const i = items.indexOf(document.activeElement as HTMLButtonElement);
        items[(i + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length]?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', close);
    };
  }, [menu, close]);

  if (!menu) return null;
  return (
    <div className="menu-layer" onClick={close} onContextMenu={(e) => (e.preventDefault(), close())}>
      <div className="menu" ref={ref} style={pos} role="menu" onClick={(e) => e.stopPropagation()}>
        {menu.title && <div className="menu__title">{menu.title}</div>}
        {menu.items.map((it) => (
          <button
            key={it.label}
            role="menuitem"
            className={`menu__item ${it.danger ? 'is-danger' : ''}`}
            disabled={it.disabled}
            onClick={() => {
              close();
              it.onSelect();
            }}
          >
            {it.icon}
            <span>{it.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** Choix d'une playlist pour y ajouter des titres */
export function PlaylistPicker() {
  const tracks = useUi((s) => s.playlistPicker);
  const pick = useUi((s) => s.pickPlaylist);
  const lists = usePlaylists();
  const { add, create } = usePlaylistMutations();
  const [name, setName] = useState('');

  useEffect(() => setName(''), [tracks]);
  useEffect(() => {
    if (!tracks) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && pick(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tracks, pick]);

  if (!tracks) return null;
  const ids = tracks.map((t) => t.id);
  const label = tracks.length === 1 ? `« ${tracks[0].title} »` : plural(tracks.length, 'titre', 'titres');

  return (
    <div className="modal-layer" onClick={() => pick(null)}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="pp-title" onClick={(e) => e.stopPropagation()}>
        <header className="modal__head">
          <h2 id="pp-title">Ajouter {label} à une playlist</h2>
          <button className="icon-btn" onClick={() => pick(null)} aria-label="Fermer">
            <X size={18} />
          </button>
        </header>
        <form
          className="modal__new"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return;
            create.mutate(
              { name: name.trim(), trackIds: ids },
              { onSuccess: () => (toast(`Playlist « ${name.trim()} » créée`), pick(null)), onError: (err) => toast(err.message, 'error') },
            );
          }}
        >
          <input className="input" placeholder="Nouvelle playlist" value={name} onChange={(e) => setName(e.target.value)} aria-label="Nom de la nouvelle playlist" autoFocus />
          <button className="btn btn--gold" type="submit" disabled={!name.trim() || create.isPending}>
            <Plus size={16} /> Créer
          </button>
        </form>
        <ul className="modal__list">
          {(lists.data ?? [])
            .filter((p) => p.canEdit && !p.smart)
            .map((p) => (
              <li key={p.id}>
                <button
                  className="modal__item"
                  onClick={() =>
                    add.mutate(
                      { id: p.id, trackIds: ids },
                      { onSuccess: () => (toast(`Ajouté à « ${p.name} »`), pick(null)), onError: (err) => toast(err.message, 'error') },
                    )
                  }
                >
                  <span>{p.name}</span>
                  <span className="muted">{plural(p.trackCount, 'titre', 'titres')}</span>
                </button>
              </li>
            ))}
          {lists.data && !lists.data.some((p) => p.canEdit && !p.smart) && <li className="muted modal__hint">Vous n’avez pas encore de playlist : créez-en une ci-dessus.</li>}
        </ul>
      </div>
    </div>
  );
}

/** Nombre de fichiers à partir duquel il faut taper SUPPRIMER */
const TYPE_TO_CONFIRM = 20;

/** Suppression définitive (administrateurs) : aperçu, confirmation, résultat */
export function DeleteDialog() {
  const req = useUi((s) => s.deleteRequest);
  const close = useUi((s) => s.askDelete);
  const [preview, setPreview] = useState<DeletePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ack, setAck] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setPreview(null);
    setError(null);
    setAck(false);
    setTyped('');
    if (!req) return;
    let live = true;
    previewDelete(req.scope)
      .then((p) => live && setPreview(p))
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [req]);
  useEffect(() => {
    if (!req) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !busy && close(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [req, busy, close]);

  if (!req) return null;
  const needType = (preview?.count ?? 0) >= TYPE_TO_CONFIRM;
  const ready = !!preview && !preview.blocked && ack && (!needType || typed.trim().toUpperCase() === 'SUPPRIMER') && !busy;

  const run = async () => {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      const r = await deleteFiles(req.scope, preview.token);
      await queryClient.invalidateQueries();
      if (r.failed.length) toast(`${plural(r.failed.length, 'fichier n’a pas pu être supprimé', 'fichiers n’ont pas pu être supprimés')} : ${r.failed[0].path} (${r.failed[0].error})`, 'error');
      toast(
        `${plural(r.deleted, 'fichier supprimé', 'fichiers supprimés')}, ${fmtBytes(r.freedBytes)} libérés` +
          (r.removedFolders ? `, ${plural(r.removedFolders, 'dossier retiré', 'dossiers retirés')}` : ''),
      );
      close(null);
      if (r.deleted) req.onDone?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-layer" onClick={() => !busy && close(null)}>
      <div className="modal modal--danger" role="alertdialog" aria-modal="true" aria-labelledby="del-title" aria-describedby="del-desc" onClick={(e) => e.stopPropagation()}>
        <header className="modal__head">
          <h2 id="del-title">Supprimer définitivement {req.label}</h2>
          <button className="icon-btn" onClick={() => close(null)} aria-label="Fermer" disabled={busy}>
            <X size={18} />
          </button>
        </header>
        <div className="modal__body" id="del-desc">
          {!preview && !error && <p className="muted">Préparation…</p>}
          {preview && (
            <>
              <p className="del__warn">
                <AlertTriangle size={18} aria-hidden />
                <span>
                  <strong>
                    {plural(preview.count, 'fichier audio', 'fichiers audio')} ({fmtBytes(preview.totalBytes)})
                  </strong>{' '}
                  seront effacés du disque du serveur. Il n’y a pas de corbeille : seule une sauvegarde permet de les retrouver.
                </span>
              </p>
              <ul className="del__files">
                {preview.files.map((f) => (
                  <li key={f}>{f}</li>
                ))}
                {preview.count > preview.files.length && <li className="muted">… et {preview.count - preview.files.length} autres</li>}
              </ul>
              {preview.folders.length > 0 && (
                <p className="muted small">
                  Dossiers retirés aussi : {preview.folders.join(', ')}
                  {preview.extras.length > 0 && <> ; avec ce qu’il y reste : {preview.extras.map((f) => f.split('/').pop()).join(', ')}</>}. Un dossier qui contient
                  d’autres fichiers (documents, photos…) est gardé.
                </p>
              )}
              <p className="muted small">Ils disparaissent aussi des favoris, des playlists et des reprises de lecture de tout le monde.</p>
              {preview.blocked ? (
                <p className="error-text">{preview.blocked}</p>
              ) : (
                <>
                  <label className="check">
                    <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
                    <span>J’ai compris : ces fichiers seront effacés pour de bon.</span>
                  </label>
                  {needType && (
                    <label className="field">
                      <span className="field__label">Tapez SUPPRIMER pour confirmer</span>
                      <input className="input" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false} />
                    </label>
                  )}
                </>
              )}
            </>
          )}
          {error && <p className="error-text">{error}</p>}
        </div>
        <footer className="modal__foot">
          <button className="btn btn--ghost" onClick={() => close(null)} disabled={busy}>
            Annuler
          </button>
          <button className="btn btn--danger-solid" onClick={() => void run()} disabled={!ready}>
            <Trash2 size={16} /> {busy ? 'Suppression…' : `Supprimer ${preview ? plural(preview.count, 'fichier', 'fichiers') : ''}`}
          </button>
        </footer>
      </div>
    </div>
  );
}

export function Toasts() {
  const toasts = useToasts((s) => s.toasts);
  return (
    <div className="toasts" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={`toast ${t.kind === 'error' ? 'toast--error' : ''}`}>
          {t.text}
        </div>
      ))}
    </div>
  );
}
